import React, { useState, useEffect } from 'react';
import AppShell from '../../components/layout/AppShell';
import api from '../../services/api';
import { shipmentApi } from '../../services/api';
import { fmtPKR, fmtDate, SERVICE_LABELS } from '../../utils/helpers';
import { Spinner, EmptyState } from '../../components/common';
import toast from 'react-hot-toast';
import { Calculator, MapPin, Package, DollarSign, Clock } from 'lucide-react';

// ─── RATE CALCULATOR PAGE (CP-06, CP-07, CP-08) ───────────────────────────────
export function RateCalculatorPage() {
  const [form, setForm] = useState({
    serviceType: 'DOM_STD', weightActual: '', lengthCm: '', widthCm: '', heightCm: '',
    declaredValue: '0', codAmount: '0', originCity: '', destCity: '',
  });
  const [result, setResult]   = useState(null);
  const [loading, setLoading] = useState(false);
  const set = k => e => setForm(f => ({ ...f, [k]: e.target.value }));

  const calculate = async () => {
    if (!form.weightActual || parseFloat(form.weightActual) <= 0) { toast.error('Enter a valid weight'); return; }
    setLoading(true);
    try {
      const res = await shipmentApi.calcRate({
        serviceType:   form.serviceType,
        weightActual:  parseFloat(form.weightActual),
        lengthCm:      form.lengthCm  ? parseFloat(form.lengthCm)  : null,
        widthCm:       form.widthCm   ? parseFloat(form.widthCm)   : null,
        heightCm:      form.heightCm  ? parseFloat(form.heightCm)  : null,
        declaredValue: parseFloat(form.declaredValue || 0),
        codAmount:     parseFloat(form.codAmount || 0),
      });
      if (res.success) setResult(res.data);
      else toast.error(res.message || 'Rate calculation failed');
    } catch (e) { toast.error(e.message || 'Failed'); }
    finally { setLoading(false); }
  };

  const SERVICES = [
    { value:'DOM_STD', label:'Standard Domestic', sub:'3-5 business days', color:'#1e40af' },
    { value:'DOM_EXP', label:'Express Domestic',  sub:'1-2 business days', color:'#7c3aed' },
    { value:'DOM_SD',  label:'Same Day',           sub:'Delivered today',   color:'#065f46' },
    { value:'DOM_ECO', label:'Economy',            sub:'5-7 business days', color:'#92400e' },
    { value:'INT_AIR', label:'International Air',  sub:'3-7 days',          color:'#1B2A4A' },
    { value:'INT_SEA', label:'International Sea',  sub:'15-30 days',        color:'#374151' },
  ];

  const volWeight = form.lengthCm && form.widthCm && form.heightCm
    ? ((parseFloat(form.lengthCm) * parseFloat(form.widthCm) * parseFloat(form.heightCm)) / 5000).toFixed(3)
    : null;
  const chargeableWeight = volWeight
    ? Math.max(parseFloat(form.weightActual || 0), parseFloat(volWeight)).toFixed(3)
    : form.weightActual;

  return (
    <AppShell title="Rate Calculator">
      <div style={{ maxWidth: 700, margin: '0 auto' }}>
        {/* Service cards */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><span className="card-title"><Calculator size={14} style={{ marginRight:6, display:'inline' }}/>Select Service Type</span></div>
          <div className="card-body">
            <div style={{ display:'grid', gridTemplateColumns:'repeat(3,1fr)', gap:8 }}>
              {SERVICES.map(s => (
                <div key={s.value} onClick={() => setForm(f => ({ ...f, serviceType: s.value }))}
                  style={{ padding:'10px 12px', border:`2px solid ${form.serviceType===s.value?s.color:'var(--gray-200)'}`,
                    borderRadius:8, cursor:'pointer', background:form.serviceType===s.value?`${s.color}10`:'white', transition:'all .15s' }}>
                  <div style={{ fontWeight:600, fontSize:'.82rem', color:s.color }}>{s.label}</div>
                  <div style={{ fontSize:'.7rem', color:'var(--gray-400)', marginTop:2 }}>{s.sub}</div>
                  {form.serviceType===s.value && <div style={{ fontSize:'.65rem', color:s.color, marginTop:3 }}>✓ Selected</div>}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Package details */}
        <div className="card" style={{ marginBottom: 16 }}>
          <div className="card-header"><span className="card-title">Package Details</span></div>
          <div className="card-body">
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">Weight (kg) <span className="req">*</span></label>
                <input className="form-control" type="number" step="0.001" min="0.001" placeholder="0.500"
                  value={form.weightActual} onChange={set('weightActual')} />
              </div>
              <div className="form-group">
                <label className="form-label">Declared Value (PKR)</label>
                <input className="form-control" type="number" min="0" placeholder="0"
                  value={form.declaredValue} onChange={set('declaredValue')} />
              </div>
            </div>
            <div className="form-group">
              <label className="form-label">Dimensions (cm) <span style={{ fontSize:'.72rem', color:'var(--gray-400)' }}>— for volumetric weight</span></label>
              <div style={{ display:'flex', gap:8 }}>
                {[['lengthCm','Length'],['widthCm','Width'],['heightCm','Height']].map(([k,p]) => (
                  <input key={k} className="form-control" type="number" min="0" placeholder={p}
                    value={form[k]} onChange={set(k)} />
                ))}
              </div>
            </div>
            <div className="form-row">
              <div className="form-group">
                <label className="form-label">COD Amount (PKR)</label>
                <input className="form-control" type="number" min="0" placeholder="0"
                  value={form.codAmount} onChange={set('codAmount')} />
              </div>
              <div className="form-group">
                <label className="form-label">Origin City</label>
                <input className="form-control" placeholder="e.g. Lahore" value={form.originCity} onChange={set('originCity')} />
              </div>
            </div>

            {/* Volumetric weight preview */}
            {volWeight && (
              <div style={{ padding:'10px 14px', background:'#EFF6FF', borderRadius:8, fontSize:'.82rem', marginTop:4 }}>
                <span style={{ color:'#1e40af', fontWeight:600 }}>Volumetric weight: {volWeight} kg</span>
                <span style={{ color:'#3b82f6', marginLeft:12 }}>Chargeable weight: <strong>{chargeableWeight} kg</strong> (higher of actual vs volumetric)</span>
              </div>
            )}

            <button className="btn btn-primary w-full" style={{ marginTop:12 }}
              onClick={calculate} disabled={loading} aria-busy={loading}>
              {loading ? 'Calculating…' : 'Calculate Rate'}
            </button>
          </div>
        </div>

        {/* Results */}
        {result && (
          <div className="card" style={{ borderTop:'3px solid var(--amber)' }}>
            <div className="card-header">
              <span className="card-title">Rate Breakdown</span>
              <span style={{ fontSize:'.78rem', color:'var(--gray-400)' }}>{SERVICE_LABELS[form.serviceType] || form.serviceType}</span>
            </div>
            <div className="card-body">
              {[
                ['Base Freight',    result.baseFreight],
                ['Fuel Surcharge',  result.fuelSurcharge],
                ['Insurance Fee',   result.insuranceFee],
                ['COD Fee',         result.codFee],
                ['Remote Surcharge',result.remoteSurcharge],
                ['Tax (GST)',       result.taxAmount],
              ].filter(([, v]) => parseFloat(v || 0) > 0).map(([l, v]) => (
                <div key={l} style={{ display:'flex', justifyContent:'space-between', padding:'7px 0', borderBottom:'1px solid var(--gray-100)', fontSize:'.85rem' }}>
                  <span style={{ color:'var(--gray-600)' }}>{l}</span>
                  <span>{fmtPKR(v)}</span>
                </div>
              ))}
              <div style={{ display:'flex', justifyContent:'space-between', padding:'12px 0 4px', fontWeight:700, borderTop:'2px solid var(--navy)', marginTop:4 }}>
                <span style={{ fontSize:'1rem' }}>Total Charges</span>
                <span style={{ fontSize:'1.3rem', color:'var(--amber)' }}>{fmtPKR(result.totalCharges)}</span>
              </div>
              {result.edd && (
                <div style={{ marginTop:10, padding:'10px 14px', background:'#F0FDF4', borderRadius:8, fontSize:'.82rem' }}>
                  <Clock size={13} style={{ marginRight:6, color:'#166534', display:'inline' }}/>
                  <span style={{ color:'#166534', fontWeight:600 }}>Estimated Delivery: {fmtDate(result.edd)}</span>
                </div>
              )}
              <div style={{ marginTop:12, display:'flex', gap:8 }}>
                <a href="/customer/book" className="btn btn-primary btn-sm">Book Now at this Rate</a>
                <button className="btn btn-outline btn-sm" onClick={() => {
                  const txt = 'ECL Rate: ' + (SERVICE_LABELS[form.serviceType]||form.serviceType) + ' — ' + fmtPKR(result.totalCharges) + ' for ' + form.weightActual + 'kg';
                  navigator.clipboard?.writeText(txt);
                  toast.success('Rate copied to clipboard');
                }}>Copy Rate</button>
              </div>
            </div>
          </div>
        )}

        {/* Corporate note */}
        <div style={{ marginTop:16, padding:'12px 16px', background:'var(--gray-50)', borderRadius:8, fontSize:'.8rem', color:'var(--gray-500)', border:'1px solid var(--gray-200)' }}>
          <strong>Corporate clients:</strong> Your contracted rates and discounts are automatically applied. Contact your account manager to update your rate agreement.
        </div>
      </div>
    </AppShell>
  );
}

// ─── CUSTOMER BILLING SUMMARY (CP-08 / 4.2.5) ────────────────────────────────
export function CustomerBillingPage() {
  const [invoices, setInvoices] = useState([]);
  const [loading, setLoading]   = useState(true);
  const [stats, setStats]       = useState({ total:0, paid:0, pending:0, overdue:0 });

  useEffect(() => {
    api.get('/invoices?limit=50').then(r => r.data).then(r => {
      if (r.success) {
        const inv = r.data || [];
        setInvoices(inv);
        setStats({
          total:   inv.reduce((s,i) => s + parseFloat(i.total||0), 0),
          paid:    inv.filter(i=>i.payment_status==='PAID').reduce((s,i)=>s+parseFloat(i.paid_amount||0),0),
          pending: inv.filter(i=>i.payment_status==='PENDING').reduce((s,i)=>s+parseFloat(i.total||0),0),
          overdue: inv.filter(i=>i.payment_status==='OVERDUE').reduce((s,i)=>s+parseFloat(i.total||0),0),
        });
      }
    }).finally(() => setLoading(false));
  }, []);

  const STATUS_COLOR = { PAID:'var(--success)', PENDING:'var(--warning)', OVERDUE:'var(--danger)', CANCELLED:'var(--gray-400)' };

  return (
    <AppShell title="Billing Summary">
      <div className="stat-grid" style={{ gridTemplateColumns:'repeat(4,1fr)', marginBottom:24 }}>
        <div className="stat-card"><div className="stat-label">Total Invoiced</div><div className="stat-value" style={{ fontSize:'1.1rem', color:'var(--navy)' }}>{fmtPKR(stats.total)}</div></div>
        <div className="stat-card"><div className="stat-label">Paid</div><div className="stat-value" style={{ fontSize:'1.1rem', color:'var(--success)' }}>{fmtPKR(stats.paid)}</div></div>
        <div className="stat-card"><div className="stat-label">Pending</div><div className="stat-value" style={{ fontSize:'1.1rem', color:'var(--warning)' }}>{fmtPKR(stats.pending)}</div></div>
        <div className="stat-card"><div className="stat-label">Overdue</div><div className="stat-value" style={{ fontSize:'1.1rem', color:'var(--danger)' }}>{fmtPKR(stats.overdue)}</div></div>
      </div>

      <div className="card">
        <div className="card-header">
          <span className="card-title">Invoice History</span>
          <a href="/customer/invoices" className="btn btn-outline btn-sm">All Invoices</a>
        </div>
        <div className="table-wrap">
          {loading ? <Spinner /> : invoices.length ? (
            <table>
              <thead>
                <tr><th>Invoice No</th><th>Period</th><th>Amount</th><th>Due Date</th><th>Status</th><th></th></tr>
              </thead>
              <tbody>
                {invoices.map(inv => (
                  <tr key={inv.invoice_id}>
                    <td><code style={{ fontSize:'.8rem', fontWeight:700, color:'var(--navy)' }}>{inv.invoice_no}</code></td>
                    <td className="text-sm">{inv.billing_period_start ? fmtDate(inv.billing_period_start)+' – '+fmtDate(inv.billing_period_end) : '—'}</td>
                    <td style={{ fontWeight:600 }}>{fmtPKR(inv.total)}</td>
                    <td className="text-sm" style={{ color:inv.payment_status==='OVERDUE'?'var(--danger)':undefined }}>
                      {inv.due_date ? fmtDate(inv.due_date) : '—'}
                    </td>
                    <td>
                      <span style={{ padding:'2px 8px', borderRadius:10, fontSize:'.68rem', fontWeight:600,
                        color:STATUS_COLOR[inv.payment_status]||'var(--gray-500)',
                        background:`${STATUS_COLOR[inv.payment_status]||'#888'}18` }}>
                        {inv.payment_status}
                      </span>
                    </td>
                    <td>
                      {(inv.payment_status==='PENDING'||inv.payment_status==='OVERDUE') && (
                        <a href="/customer/invoices" className="btn btn-success btn-sm" style={{ fontSize:'.7rem' }}>
                          <DollarSign size={10}/> Pay
                        </a>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : <EmptyState icon="📄" title="No invoices yet" />}
        </div>
      </div>
    </AppShell>
  );
}

// ─── ENHANCED TRACK PAGE (CP-09, CP-10, CP-11) ───────────────────────────────
// Timeline-focused version for the customer portal (/customer/track)
export function CustomerTrackPage() {
  const [tid, setTid]       = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [error, setError]   = useState('');

  const track = async () => {
    const q = tid.trim().toUpperCase();
    if (!q) return;
    setLoading(true); setError(''); setResult(null);
    try {
      const res = await shipmentApi.track(q);
      if (res.success) setResult(res.data);
      else setError('Shipment not found');
    } catch { setError('Shipment not found. Please check the tracking ID.'); }
    finally { setLoading(false); }
  };

  const s = result?.shipment;
  const timeline = result?.statusHistory || result?.timeline || [];

  const ALL_STATUSES = [
    { key:'BOOKING_CONFIRMED',  label:'Booked',          icon:'📋' },
    { key:'PICKUP_SCHEDULED',   label:'Pickup Scheduled', icon:'📅' },
    { key:'PICKED_UP',          label:'Picked Up',        icon:'🤝' },
    { key:'IN_TRANSIT',         label:'In Transit',       icon:'🚚' },
    { key:'AT_HUB',             label:'At Hub',           icon:'🏭' },
    { key:'OUT_FOR_DELIVERY',   label:'Out for Delivery', icon:'🛵' },
    { key:'DELIVERED',          label:'Delivered',        icon:'✅' },
  ];

  const currentIdx = s ? ALL_STATUSES.findIndex(x => x.key === s.status) : -1;

  return (
    <AppShell title="Track Shipment">
      <div style={{ maxWidth:720, margin:'0 auto' }}>
        {/* Search bar */}
        <div style={{ display:'flex', gap:8, marginBottom:24 }}>
          <input className="form-control" style={{ fontSize:'1rem', padding:'12px 16px' }}
            placeholder="Enter tracking ID — e.g. ECLPK202604130000001"
            value={tid} onChange={e => setTid(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && track()} />
          <button className="btn btn-primary" style={{ minWidth:100 }} onClick={track} disabled={loading}>
            {loading ? '…' : '🔍 Track'}
          </button>
        </div>

        {error && <div className="alert alert-danger"><Package size={16} style={{ marginRight:8, display:'inline' }}/>{error}</div>}

        {result && s && (
          <>
            {/* Header card */}
            <div className="card" style={{ marginBottom:16 }}>
              <div className="card-body">
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', flexWrap:'wrap', gap:12 }}>
                  <div>
                    <div style={{ fontSize:'.7rem', color:'var(--gray-400)', textTransform:'uppercase', marginBottom:4 }}>Tracking ID</div>
                    <div style={{ fontFamily:'monospace', fontWeight:700, fontSize:'1.1rem', color:'var(--navy)' }}>{s.tracking_id}</div>
                  </div>
                  <div style={{ textAlign:'right' }}>
                    <div style={{ fontWeight:700, color:'var(--navy)', fontSize:'.9rem' }}>
                      {SERVICE_LABELS[s.service_type] || s.service_type}
                    </div>
                    {s.edd && (
                      <div style={{ fontSize:'.78rem', color:'var(--gray-500)', marginTop:3 }}>
                        EDD: <strong>{fmtDate(s.edd)}</strong>
                      </div>
                    )}
                  </div>
                </div>

                {/* Progress stepper */}
                {!['CANCELLED','LOST','DAMAGED','RTO_INITIATED','RTO_IN_TRANSIT','RTO_DELIVERED'].includes(s.status) && (
                  <div style={{ marginTop:20 }}>
                    <div style={{ display:'flex', alignItems:'center' }}>
                      {ALL_STATUSES.map((st, i) => {
                        const done    = i < currentIdx;
                        const current = i === currentIdx;
                        const isLast  = i === ALL_STATUSES.length - 1;
                        return (
                          <React.Fragment key={st.key}>
                            <div style={{ display:'flex', flexDirection:'column', alignItems:'center', flex:'none' }}>
                              <div style={{ width:32, height:32, borderRadius:'50%', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'.85rem',
                                background: done?'var(--success)':current?'var(--amber)':'var(--gray-100)',
                                border:`2px solid ${done?'var(--success)':current?'var(--amber)':'var(--gray-200)'}`,
                                fontWeight:700, color: (done||current)?'#fff':'var(--gray-300)' }}>
                                {done ? '✓' : st.icon}
                              </div>
                              <div style={{ fontSize:'.6rem', color: current?'var(--amber)':done?'var(--success)':'var(--gray-400)', marginTop:4, fontWeight:current?700:400, textAlign:'center', maxWidth:52 }}>
                                {st.label}
                              </div>
                            </div>
                            {!isLast && (
                              <div style={{ flex:1, height:2, background:done?'var(--success)':'var(--gray-200)', margin:'0 2px', marginBottom:18 }}/>
                            )}
                          </React.Fragment>
                        );
                      })}
                    </div>
                  </div>
                )}

                {/* RTO / special status */}
                {['RTO_INITIATED','RTO_IN_TRANSIT','RTO_DELIVERED'].includes(s.status) && (
                  <div style={{ marginTop:16, padding:'10px 14px', background:'#FEF2F2', borderRadius:8, color:'var(--danger)', fontSize:'.85rem', fontWeight:600 }}>
                    🔄 Return to Origin — {s.status.replace(/_/g,' ')}
                  </div>
                )}

                {/* Info grid */}
                <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:12, marginTop:16, paddingTop:16, borderTop:'1px solid var(--gray-100)' }}>
                  {[
                    ['From', s.pickup_address   ? (typeof s.pickup_address==='object'   ? s.pickup_address.city   : s.pickup_address)  : '—'],
                    ['To',   s.delivery_address ? (typeof s.delivery_address==='object' ? s.delivery_address.city : s.delivery_address): '—'],
                    ['Weight', s.weight_actual ? s.weight_actual+' kg' : '—'],
                    ['Billing', s.billing_model || '—'],
                    ...(s.cod_amount>0 ? [['COD','PKR '+Number(s.cod_amount).toLocaleString()]] : []),
                    ...(s.delivered_at ? [['Delivered', fmtDate(s.delivered_at)]] : []),
                  ].map(([l,v]) => (
                    <div key={l}>
                      <div style={{ fontSize:'.68rem', color:'var(--gray-400)', textTransform:'uppercase', marginBottom:2 }}>{l}</div>
                      <div style={{ fontSize:'.85rem', fontWeight:600 }}>{v}</div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* OUT FOR DELIVERY rider card */}
            {s.status === 'OUT_FOR_DELIVERY' && (
              <div className="card" style={{ marginBottom:16, borderLeft:'4px solid var(--amber)' }}>
                <div className="card-body">
                  <div style={{ fontWeight:600, color:'var(--amber)', marginBottom:10, display:'flex', alignItems:'center', gap:8 }}>
                    <MapPin size={16}/> Your shipment is out for delivery today!
                  </div>
                  {/* Live map placeholder — future-ready GPS (CP-10) */}
                  <div style={{ background:'#F0FDF4', border:'2px dashed #86EFAC', borderRadius:10, padding:'24px', textAlign:'center', marginBottom:12 }}>
                    <div style={{ fontSize:32, marginBottom:6 }}>🗺️</div>
                    <div style={{ fontWeight:600, color:'#166534', fontSize:'.85rem' }}>Live Map Tracking</div>
                    <div style={{ color:'#4ADE80', fontSize:'.75rem', marginTop:4 }}>GPS live tracking will appear here when rider location is available</div>
                  </div>
                  <div style={{ display:'flex', gap:16, fontSize:'.82rem' }}>
                    {result.riderInfo?.name && <div><span style={{ color:'var(--gray-400)' }}>Rider: </span><strong>{result.riderInfo.name}</strong></div>}
                    {result.riderInfo?.mobile && (
                      <div><span style={{ color:'var(--gray-400)' }}>Contact: </span>
                        <a href={'tel:'+result.riderInfo.mobile} style={{ color:'var(--navy)', fontWeight:600 }}>{result.riderInfo.mobile}</a>
                      </div>
                    )}
                    <div style={{ color:'var(--gray-400)', fontSize:'.72rem' }}>Updates every 60 seconds</div>
                  </div>
                </div>
              </div>
            )}

            {/* Event timeline */}
            <div className="card">
              <div className="card-header">
                <span className="card-title">Event Timeline</span>
                <span style={{ fontSize:'.75rem', color:'var(--gray-400)' }}>{timeline.length} events</span>
              </div>
              <div className="card-body">
                {timeline.length ? (
                  <div style={{ position:'relative', paddingLeft:20 }}>
                    <div style={{ position:'absolute', left:8, top:8, bottom:8, width:2, background:'var(--gray-100)' }}/>
                    {[...timeline].reverse().map((item, i) => {
                      const isFirst = i === 0;
                      return (
                        <div key={i} style={{ display:'flex', gap:16, marginBottom:16, position:'relative' }}>
                          <div style={{ position:'absolute', left:-16, top:4, width:12, height:12, borderRadius:'50%',
                            background:isFirst?'var(--amber)':'var(--success)',
                            border:`2px solid ${isFirst?'var(--amber)':'var(--success)'}`,
                            flexShrink:0 }}/>
                          <div style={{ flex:1 }}>
                            <div style={{ fontWeight:isFirst?700:500, fontSize:'.85rem', color:isFirst?'var(--navy)':'var(--gray-700)' }}>
                              {item.status?.replace(/_/g,' ')}
                            </div>
                            {item.hub_name && (
                              <div style={{ fontSize:'.75rem', color:'var(--gray-400)', marginTop:2 }}>
                                <MapPin size={10} style={{ display:'inline', marginRight:3 }}/>{item.hub_name}
                              </div>
                            )}
                            {item.notes && <div style={{ fontSize:'.75rem', color:'var(--gray-500)', marginTop:2, fontStyle:'italic' }}>{item.notes}</div>}
                            <div style={{ fontSize:'.72rem', color:'var(--gray-400)', marginTop:3 }}>
                              {new Date(item.created_at).toLocaleString('en-PK', { dateStyle:'medium', timeStyle:'short' })}
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                ) : (
                  <div style={{ textAlign:'center', padding:24, color:'var(--gray-400)' }}>No events recorded yet</div>
                )}
              </div>
            </div>

            {/* Actions */}
            <div style={{ display:'flex', gap:8, justifyContent:'center', marginTop:12, flexWrap:'wrap' }}>
              <button className="btn btn-outline btn-sm" onClick={() => { navigator.clipboard?.writeText(window.location.href); toast.success('Link copied!'); }}>
                🔗 Copy Link
              </button>
              <a href="/customer/tickets" className="btn btn-outline btn-sm">🎫 Raise Issue</a>
              <button className="btn btn-outline btn-sm" onClick={() => window.print()}>🖨️ Print</button>
            </div>
          </>
        )}
      </div>
    </AppShell>
  );
}
